import { Injectable } from '@angular/core';

export interface CookieOptions {
	expires?: Date | number | string;
	path?: string;
	domain?: string;
	secure?: boolean;
	sameSite?: 'strict' | 'lax' | 'none';
	maxAge?: number;
}

@Injectable({
	providedIn: 'root'
})
export class StorageService {

	constructor() { }

	/**
	 * Retrieves all key-value pairs from local storage.
	 * @returns An array of objects, each containing a `key` and its corresponding `value`.
	 */
	allLocal(): Array<{ key: string, value: string }> {
		try {
			const items: Array<{ key: string, value: string }> = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i) || '';
				const value = localStorage.getItem(key) || '';
				items.push({ key, value });
			}
			return items;
		}
		catch (error) {
			console.error('Error while fetching all local storage items:', error);
			return [];
		}
	}

	/**
	 * Retrieves a single item from local storage.
	 * @param name The key of the local storage item.
	 * @returns The value associated with the given key, or `null` if not found.
	 */
	getLocal(name: string): string | null {
		try {
			return localStorage.getItem(name);
		}
		catch (error) {
			console.error(`Error while getting local storage item with key "${name}":`, error);
			return null;
		}
	}

	/**
	 * Adds a new item to local storage.
	 * @param name The key for the new local storage item.
	 * @param value The value to store.
	 * @returns `true` if the item was successfully added; `false` if an item with the same key already exists or an error occurred.
	 */
	addLocal(name: string, value: string): boolean {
		try {
			if (localStorage.getItem(name)) {
				console.error(`Local storage item with key "${name}" already exists.`);
				return false;
			}
			localStorage.setItem(name, value);
			return true;
		}
		catch (error) {
			console.error(`Error while adding local storage item with key "${name}":`, error);
			return false;
		}
	}

	/**
	 * Updates the value of an existing local storage item.
	 * @param name The key of the local storage item to update.
	 * @param value The new value for the item.
	 * @returns `true` if the item was successfully updated; `false` if the item does not exist or an error occurred.
	 */
	changeLocal(name: string, value: string): boolean {
		try {
			if (!localStorage.getItem(name)) {
				console.error(`Local storage item with key "${name}" does not exist.`);
				return false;
			}
			localStorage.setItem(name, value);
			return true;
		}
		catch (error) {
			console.error(`Error while updating local storage item with key "${name}":`, error);
			return false;
		}
	}

	/**
	 * Deletes a specific item from local storage.
	 * @param name The key of the item to delete.
	 * @returns `true` if the item was successfully deleted; `false` if the item does not exist or an error occurred.
	 */
	deleteLocal(name: string): boolean {
		try {
			if (!localStorage.getItem(name)) {
				console.error(`Local storage item with key "${name}" does not exist.`);
				return false;
			}
			localStorage.removeItem(name);
			return true;
		}
		catch (error) {
			console.error(`Error while deleting local storage item with key "${name}":`, error);
			return false;
		}
	}

	/**
	 * Clears all items from local storage.
	 * @returns `true` if all items were successfully cleared; `false` if an error occurred.
	 */
	clearLocal(): boolean {
		try {
			localStorage.clear();
			return true;
		}
		catch (error) {
			console.error('Error while clearing all local storage items:', error);
			return false;
		}
	}


	/** ---------- COOKIES ---------- */

	/**
	 * Retrieves all cookies as an array of key-value pairs.
	 * @returns An array of objects, each containing `key` and `value` for a cookie.
	 */
	allCookie(): Array<{ key: string, value: string }> {
		try {
			const cookies: Array<{ key: string, value: string }> = [];
			const decodedCookies = decodeURIComponent(document.cookie).split(';');
			for (const cookie of decodedCookies) {
				const [key, value] = cookie.split('=').map(c => c.trim());
				if (key) {cookies.push({ key, value: value || '' });}
			}
			return cookies;
		}
		catch (error) {
			console.error('Error while fetching all cookies:', error);
			return [];
		}
	}

	/**
	 * Retrieves a cookie value by its name.
	 * @param name The name of the cookie to retrieve.
	 * @returns The cookie value, or `null` if not found.
	 */
	getCookie(name: string): string | null {
		try {
			const cookies = this.allCookie();
			const cookie = cookies.find(c => c.key === name);
			return cookie ? cookie.value : null;
		}
		catch (error) {
			console.error(`Error while getting cookie with name "${name}":`, error);
			return null;
		}
	}

	/**
	 * Adds a new cookie if it does not already exist.
	 * @param name The name of the cookie to add.
	 * @param value The value of the cookie.
	 * @param options Optional configuration for the cookie (expires, path, domain, secure, sameSite, maxAge).
	 * @returns `true` if the cookie was successfully added; `false` if a cookie with the same name already exists or an error occurred.
	 */
	addCookie(name: string, value: string, options?: CookieOptions): boolean {
		try {
			if (this.getCookie(name)) {
				console.error(`Cookie with name "${name}" already exists.`);
				return false;
			}
			const cookieString = this.buildCookieString(name, value, options);
			document.cookie = cookieString;
			return true;
		}
		catch (error) {
			console.error(`Error while adding cookie with name "${name}":`, error);
			return false;
		}
	}

	/**
	 * Updates the value of an existing cookie.
	 * @param name The name of the cookie to update.
	 * @param value The new value for the cookie.
	 * @param options Optional configuration for the cookie (expires, path, domain, secure, sameSite, maxAge).
	 * @returns `true` if the cookie was successfully updated; `false` if the cookie does not exist or an error occurred.
	 */
	changeCookie(name: string, value: string, options?: CookieOptions): boolean {
		try {
			if (!this.getCookie(name)) {
				console.error(`Cookie with name "${name}" does not exist.`);
				return false;
			}
			const cookieString = this.buildCookieString(name, value, options);
			document.cookie = cookieString;
			return true;
		}
		catch (error) {
			console.error(`Error while updating cookie with name "${name}":`, error);
			return false;
		}
	}

	/**
	 * Updates the expiration date of the "token" cookie by the specified number of minutes.
	 * @param minutes The number of minutes to add to the current time for the new expiration date.
	 * @returns `true` if the token expiration was successfully updated; `false` otherwise.
	 */
	updateTokenExpiration(minutes: number): boolean {
		try {
			const tokenValue = this.getCookie('token');
			if (!tokenValue) {
				console.error('No cookie named "token" found.');
				return false;
			}
			const newDate = new Date();
			newDate.setMinutes(newDate.getMinutes() + minutes);
			return this.changeCookie('token', tokenValue, { expires: newDate });
		}
		catch (error) {
			console.error('Error while updating token expiration date:', error);
			return false;
		}
	}

	/**
	 * Deletes a cookie by its name.
	 * @param name The name of the cookie to delete.
	 * @returns `true` if the cookie was successfully deleted; `false` if the cookie does not exist or an error occurred.
	 */
	deleteCookie(name: string): boolean {
		try {
			if (!this.getCookie(name)) {
				console.error(`Cookie with name "${name}" does not exist.`);
				return false;
			}
			document.cookie = `${encodeURIComponent(name)}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
			return true;
		}
		catch (error) {
			console.error(`Error while deleting cookie with name "${name}":`, error);
			return false;
		}
	}

	/**
	 * Clears all cookies.
	 * @returns `true` if all cookies were successfully cleared; `false` if an error occurred.
	 */
	clearCookie(): boolean {
		try {
			const cookies = this.allCookie();
			cookies.forEach(cookie => {this.deleteCookie(cookie.key);});
			return true;
		}
		catch (error) {
			console.error('Error while clearing all cookies:', error);
			return false;
		}
	}

	/**
	 * Builds the string that will be assigned to `document.cookie`, taking into account various cookie options.
	 * @param name The name of the cookie.
	 * @param value The value of the cookie.
	 * @param options Additional options for configuring the cookie.
	 * @returns A properly formatted string for creating or updating a cookie.
	 */
	private buildCookieString(name: string, value: string, options?: CookieOptions): string {
		let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)};`;
		if (!options) {cookieString += 'path=/;'; return cookieString;}
		if (options.expires !== undefined) {
			if (typeof options.expires === 'number') {
				const date = new Date();
				date.setDate(date.getDate() + options.expires);
				cookieString += `expires=${date.toUTCString()};`;
			}
			else if (options.expires instanceof Date) {cookieString += `expires=${options.expires.toUTCString()};`;}
			else {
				const parsedDate = new Date(options.expires);
				if (!isNaN(parsedDate.getTime())) {cookieString += `expires=${parsedDate.toUTCString()};`;}
			}
		}
		if (options.maxAge !== undefined) {cookieString += `max-age=${options.maxAge};`;}
		if (options.path) {cookieString += `path=${options.path};`;}
		else {cookieString += `path=/;`;}
		if (options.domain) {cookieString += `domain=${options.domain};`;}
		if (options.sameSite) {cookieString += `SameSite=${options.sameSite};`;}
		if (options.secure) {cookieString += 'secure;';}
		return cookieString;
	}
}